import { useListingStepNavigation } from '@/lib/hooks/use-listing-step-navigation'
/**
 * New Listing — Step 2: Features
 *
 * Collects title, description, feature checkboxes, and instant-book toggle.
 */
import React, { useState, useCallback } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import { Zap, Sparkles } from '@/components/ui/icons'
import { notification, selection, NotificationType } from '@/lib/utils/haptics'
import { toast } from '@/lib/toast'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { Chip } from '@/components/ui/Chip'
import { Pressable } from '@/components/ui/Pressable'
import { ToggleTrack } from '@/components/ui/ToggleTrack'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { useListingWizardStore } from '@/lib/stores/listing-wizard.store'
import { useMakes, useModels } from '@/lib/hooks/use-host-vehicles'
import { vehicleFeaturesSchema, VEHICLE_FEATURES } from '@/lib/validations/vehicle.schema'
import { useSemanticColors, SPACING, RADIUS, INTERACTION, FORM_FIELD_GAP } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { COLORS, FONTS } from '@/lib/brand'
import { titleCase, sentenceCase } from '@/lib/utils/sanitize'
import { vehicleAIService } from '@/lib/services/vehicle-ai.service'
import { addBreadcrumb } from '@/lib/utils/observability'
import { marketTransmissionI18nKey, toMarketTransmission } from '@/lib/utils/transmission'
import { useEntrance } from '@/lib/hooks/use-entrance'

// ─── Screen ─────────────────────────────────────────────────────────────────

export default function NewListingFeaturesScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { continueEditing, continueLabel } = useListingStepNavigation('photos')
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  // Wizard store
  const details = useListingWizardStore((s) => s.details)
  const features = useListingWizardStore((s) => s.features)
  const setFeatures = useListingWizardStore((s) => s.setFeatures)

  // Resolve make/model names for auto-generated title
  const { data: makes } = useMakes()
  const { data: models } = useModels(details.makeId)
  const makeName = makes?.find((m) => m.id === details.makeId)?.name ?? ''
  const modelName = models?.find((m) => m.id === details.modelId)?.name ?? ''

  // Auto title/description: local fallback first (instant, zero friction), then
  // one silent AI upgrade. Never overwrite after the host types.
  const autoGenDone = React.useRef(false)
  const autoAiDone = React.useRef(false)
  const hostEditedCopy = React.useRef(Boolean(features.title || features.description))
  const [isGenerating, setIsGenerating] = useState(false)

  const buildLocalCopy = useCallback(() => {
    const rawTitle = `${makeName} ${modelName} ${details.year}`.trim()
    // Schema requires title ≥ 10 chars — pad with year/make only when OCR left gaps.
    const title =
      rawTitle.length >= 10 ? rawTitle : `${rawTitle} ${t('listing.auto_title_suffix')}`.trim()

    const specs: string[] = []
    const transmissionKey = marketTransmissionI18nKey('listing.transmissions', details.transmission)
    if (transmissionKey) specs.push(t(transmissionKey).toLowerCase())
    if (details.fuelType) specs.push(t(`listing.fuel_types.${details.fuelType}`).toLowerCase())
    if (details.seats) specs.push(`${details.seats} ${t('listing.seats').toLowerCase()}`)
    if (details.doors) specs.push(`${details.doors} ${t('listing.doors').toLowerCase()}`)
    if (details.color) specs.push(titleCase(details.color))

    const specLine = specs.length > 0 ? ` ${sentenceCase(specs.join(', '))}.` : ''
    const category = details.category ? ` ${t(`listing.categories.${details.category}`)}` : ''
    let description = `${makeName} ${modelName} ${details.year}${category}.${specLine}`.trim()
    // Schema requires description ≥ 30 when present — keep publish unblocked offline.
    if (description.length > 0 && description.length < 30) {
      description = `${description} ${t('listing.auto_description_suffix')}`.trim()
    }
    return { title, description }
  }, [makeName, modelName, details, t])

  const runAiCopy = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!makeName || !modelName || !details.year) return
      if (!opts?.silent && hostEditedCopy.current === false) {
        // Manual regenerate still allowed after auto path.
      }
      setIsGenerating(true)
      if (!opts?.silent) void selection()

      try {
        const result = await vehicleAIService.generateDescription({
          make: makeName,
          model: modelName,
          year: details.year,
          category: details.category,
          color: details.color,
          transmission: toMarketTransmission(details.transmission) ?? undefined,
          fuelType: details.fuelType,
          seats: details.seats,
          doors: details.doors,
          features: features.features,
          locale: t('common.app_name') === 'Tago' ? 'es' : 'en',
        })

        // Manual regenerate always applies. Silent auto only applies if the host
        // has not typed over the generated copy.
        if (!opts?.silent || !hostEditedCopy.current) {
          if (result.title) setFeatures({ title: result.title })
          if (result.description) setFeatures({ description: result.description })
        }
        if (!opts?.silent) void notification(NotificationType.Success)
      } catch (err) {
        if (!opts?.silent) {
          toast.error(t('listing.ai_generation_failed'))
        }
        // La causa raíz la reporta el edge `generate-description` con su
        // contexto real (proveedor, estado HTTP). Capturarla también acá
        // duplicaba el mismo intento como un segundo issue en Sentry
        // (TAGO-MOBILE-5G al lado de 5F, mismo minuto): acá queda breadcrumb.
        addBreadcrumb('ai', 'generate-description failed', {
          action: opts?.silent ? 'ai-generate-auto' : 'ai-generate',
          reason: err instanceof Error ? err.name : 'unknown',
        })
      } finally {
        setIsGenerating(false)
      }
    },
    [makeName, modelName, details, features.features, setFeatures, t],
  )

  React.useEffect(() => {
    if (autoGenDone.current) return
    if (!makeName || !modelName || !details.year) return

    autoGenDone.current = true
    const local = buildLocalCopy()

    // Title: "Toyota Corolla 2023" — the name of the car, nothing else.
    if (!features.title) {
      setFeatures({ title: local.title })
    }
    if (!features.description) {
      setFeatures({ description: local.description })
    }
  }, [
    makeName,
    modelName,
    details,
    features.title,
    features.description,
    setFeatures,
    buildLocalCopy,
  ])

  // One automatic AI pass after local copy is ready. Fails soft — host already
  // has a complete, publishable title + description without waiting on the model.
  React.useEffect(() => {
    if (autoAiDone.current) return
    if (!autoGenDone.current) return
    if (!makeName || !modelName || !details.year) return
    if (hostEditedCopy.current) return

    autoAiDone.current = true
    void runAiCopy({ silent: true })
  }, [makeName, modelName, details.year, runAiCopy])

  const handleGenerateDescription = useCallback(() => {
    void runAiCopy({ silent: false })
  }, [runAiCopy])

  // Local validation errors
  const [errors, setErrors] = useState<Record<string, string>>({})

  // ── Derived ─────────────────────────────────────────────────────────────

  const titleLength = features.title?.length ?? 0
  const descriptionLength = features.description?.length ?? 0
  const selectedFeatures = features.features ?? []

  // ── Handlers ────────────────────────────────────────────────────────────

  const toggleFeature = useCallback(
    (feature: string) => {
      void selection()
      const current = features.features ?? []
      const next = current.includes(feature)
        ? current.filter((f) => f !== feature)
        : [...current, feature]
      setFeatures({ features: next })
      setErrors((e) => ({ ...e, features: '' }))
    },
    [features.features, setFeatures],
  )

  const handleInstantBookToggle = useCallback(
    (value: boolean) => {
      void selection()
      setFeatures({ isInstantBook: value })
    },
    [setFeatures],
  )

  const handleNext = useCallback(() => {
    const result = vehicleFeaturesSchema.safeParse({
      title: features.title ?? '',
      description: features.description || undefined,
      features: features.features ?? [],
      rules: features.rules || undefined,
      isInstantBook: features.isInstantBook ?? false,
    })

    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string
        fieldErrors[field] = t(issue.message)
      }
      setErrors(fieldErrors)
      return
    }

    setFeatures(result.data)
    continueEditing()
  }, [features, setFeatures, continueEditing, t])

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAwareView includeTopInset={false}>
        {/* Title */}
        <Animated.View collapsable={false} entering={enter(1)}>
          {/* TextInput ships its own FORM_FIELD_GAP bottom margin. With the
              counter rendered as a plain sibling it landed 32pt under the
              field and only a few points above the NEXT label, so it read as a
              heading for the section below rather than as this field's
              counter. Cancelling the gap here makes the counter the field's
              footer; the section gap falls after the pair. */}
          <View style={{ marginBottom: -FORM_FIELD_GAP }}>
            <TextInput
              label={t('listing.title')}
              placeholder={t('listing.title_placeholder')}
              value={features.title ?? ''}
              onChangeText={(title) => {
                hostEditedCopy.current = true
                setFeatures({ title })
                setErrors((e) => ({ ...e, title: '' }))
              }}
              maxLength={80}
              error={errors.title}
              testID="listing-features.title-input"
            />
          </View>
          <Text
            style={{
              fontSize: 12,
              fontFamily: FONTS.body,
              color: titleLength > 70 ? colors.textAccent : colors.textMuted,
              textAlign: 'right',
              marginTop: SPACING.xs,
            }}
          >
            {titleLength}/80
          </Text>
        </Animated.View>

        {/* Description */}
        <Animated.View
          collapsable={false}
          entering={enter(2)}
          style={{ marginTop: FORM_FIELD_GAP }}
        >
          {/* Same footer treatment as the title field — see the note above. */}
          <View style={{ marginBottom: -FORM_FIELD_GAP }}>
            <TextInput
              label={t('listing.description')}
              placeholder={t('listing.description_placeholder')}
              value={features.description ?? ''}
              onChangeText={(description) => {
                hostEditedCopy.current = true
                setFeatures({ description })
                setErrors((e) => ({ ...e, description: '' }))
              }}
              multiline
              numberOfLines={4}
              maxLength={1000}
              // `style` REPLACES TextInput's own input style (it is spread
              // after it), so the layout and type rules have to be restated
              // here. Without `flex: 1` the textarea shrank to its content and
              // the placeholder wrapped into three ragged lines at ~55% of the
              // box, with a wide empty margin on the right.
              style={{
                flex: 1,
                alignSelf: 'stretch',
                minHeight: 100,
                textAlignVertical: 'top',
                paddingVertical: SPACING.md,
                color: colors.textPrimary,
                fontFamily: FONTS.body,
                fontSize: 15,
              }}
              error={errors.description}
              testID="listing-features.description-input"
            />
          </View>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: SPACING.xs,
            }}
          >
            <Pressable
              onPress={handleGenerateDescription}
              disabled={isGenerating || !makeName || !modelName || !details.year}
              accessibilityRole="button"
              accessibilityLabel={
                isGenerating ? t('listing.ai_generating') : t('listing.ai_generate_description')
              }
              testID="listing-features.ai-generate"
              style={{
                minHeight: INTERACTION.minTouchTarget,
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.xs,
                opacity: isGenerating || !makeName ? 0.5 : 1,
              }}
            >
              <Sparkles size={14} color={COLORS.coral.DEFAULT} />
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: FONTS.body,
                  color: colors.textAccent,
                }}
              >
                {isGenerating ? t('listing.ai_generating') : t('listing.ai_regenerate')}
              </Text>
            </Pressable>
            <Text
              style={{
                fontSize: 12,
                fontFamily: FONTS.body,
                color: descriptionLength > 900 ? colors.textAccent : colors.textMuted,
              }}
            >
              {descriptionLength}/1000
            </Text>
          </View>
        </Animated.View>

        {/* Features multi-select */}
        <Animated.View
          collapsable={false}
          entering={enter(3)}
          style={{ marginTop: FORM_FIELD_GAP }}
        >
          <Text
            style={{
              fontSize: 13,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              marginBottom: SPACING.sm,
              letterSpacing: 0.2,
            }}
          >
            {t('listing.features_label')}
          </Text>
          <Text
            style={{
              fontSize: 12,
              fontFamily: FONTS.body,
              color: colors.textMuted,
              marginBottom: SPACING.sm,
            }}
          >
            {t('listing.features_hint')}
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
            {VEHICLE_FEATURES.map((feature) => {
              const isSelected = selectedFeatures.includes(feature)
              return (
                <Chip
                  key={feature}
                  label={t(`listing.vehicle_features.${feature}`)}
                  selected={isSelected}
                  onPress={() => toggleFeature(feature)}
                  variant={isSelected ? 'filled' : 'outlined'}
                  color={isSelected ? 'primary' : 'default'}
                />
              )
            })}
          </View>
          {errors.features ? (
            <Text
              style={{
                color: colors.textAccent,
                fontSize: 12,
                fontFamily: FONTS.body,
                marginTop: SPACING.xs,
              }}
            >
              {errors.features}
            </Text>
          ) : null}
        </Animated.View>

        {/* Instant Book toggle */}
        <Animated.View collapsable={false} entering={enter(4)} style={{ marginTop: SPACING.lg }}>
          <Pressable
            onPress={() => handleInstantBookToggle(!features.isInstantBook)}
            accessibilityRole="switch"
            accessibilityState={{ checked: features.isInstantBook ?? false }}
            accessibilityLabel={t('listing.instant_book')}
            testID="listing-features.instant-book"
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: colors.surface,
              borderRadius: RADIUS.lg,
              padding: SPACING.md,
              borderWidth: 1,
              borderColor: features.isInstantBook ? COLORS.coral.DEFAULT : colors.border,
              opacity: pressed ? 0.76 : 1,
            })}
          >
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: RADIUS.md,
                backgroundColor: features.isInstantBook
                  ? `${COLORS.coral.DEFAULT}15`
                  : colors.surfaceSubtle,
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: SPACING.md,
              }}
            >
              <Zap
                size={20}
                color={features.isInstantBook ? COLORS.coral.DEFAULT : colors.textSecondary}
                fill={features.isInstantBook ? COLORS.coral.DEFAULT : 'none'}
                strokeWidth={1.8}
              />
            </View>
            <View style={{ flex: 1, marginRight: SPACING.sm }}>
              <Text
                style={{
                  fontSize: 15,
                  fontFamily: FONTS.body,
                  color: colors.textPrimary,
                }}
              >
                {t('listing.instant_book')}
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  marginTop: 2,
                }}
              >
                {t('listing.instant_book_desc')}
              </Text>
            </View>
            {/* See components/ui/ToggleTrack: RN's Switch is a real <input> on
                web that stays focusable and unnamed inside the row that is the
                actual control. */}
            <ToggleTrack value={features.isInstantBook ?? false} isDark={isDark} />
          </Pressable>
        </Animated.View>

        {/* Bottom spacer for scroll */}
        <View style={{ height: 100 }} />
      </KeyboardAwareView>

      <FormActionBar style={{ backgroundColor: colors.background }}>
        <Button
          onPress={handleNext}
          variant="primary"
          size="lg"
          title={continueLabel}
          testID="listing-features.next-button"
        />
      </FormActionBar>
    </ScreenContainer>
  )
}
